import { gql, useMutation, useQuery, useApolloClient } from "@apollo/client";
import React, { useState, useContext, useEffect } from "react";
import {
  LoadingView,
  InvalidReasons,
  NotificationContext,
  ToggleSwitch
} from "@solsys/solsys-reactjs";
import SaveButton from "../elements/SaveButton";
import {
  useCreateIdempotencyKeys,
  fingerprintProperties,
  mutationInput,
} from "../../utils/idempotency";
import styled from "styled-components";
import { FaTimes } from "react-icons/fa";

// Styled Components
const MultiSelectContainer = styled.div.attrs({ className: "service-definition-multiselect-container" })`
  margin-top: 20px;
  padding: 20px;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
`;

const MultiSelectHeader = styled.div.attrs({ className: "service-definition-multiselect-header" })`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #dee2e6;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #212529;
  }
`;

const SearchInput = styled.input.attrs({ className: "service-definition-search-input" })`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  margin-bottom: 15px;
  
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const ItemsList = styled.div.attrs({ className: "service-definition-items-list" })`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 8px;
  background: #f8f9fa;
`;

const ItemCheckbox = styled.label.attrs({ className: "service-definition-item-checkbox" })`
  display: flex;
  align-items: center;
  padding: 8px 10px;
  margin-bottom: 4px;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #e9ecef;
    border-color: #adb5bd;
  }
  
  input[type="checkbox"] {
    margin-right: 10px;
    cursor: pointer;
  }
  
  .item-info {
    flex: 1;
    
    .item-name {
      font-weight: 500;
      font-size: 14px;
      color: #212529;
      margin-bottom: 2px;
    }
    
    .item-meta {
      font-size: 12px;
      color: #6c757d;
      display: flex;
      gap: 10px;
    }
  }
`;

const SelectedItems = styled.div.attrs({ className: "service-definition-selected-items" })`
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #dee2e6;
  
  h4 {
    margin: 0 0 10px 0;
    font-size: 14px;
    font-weight: 600;
    color: #495057;
  }
`;

const SelectedItemTag = styled.span.attrs({ className: "service-definition-selected-item-tag" })`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  margin: 4px;
  background: #e7f3ff;
  border: 1px solid #b3d9ff;
  border-radius: 4px;
  font-size: 13px;
  color: #004085;
  
  button {
    background: none;
    border: none;
    color: #004085;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    
    &:hover {
      color: #002752;
    }
  }
`;

const entityQuery = gql`
  query service_definitionList(
    $service_definition_list_request: service_definition_list_request
    $serviceDefinition2ServiceItem_list_request: serviceDefinition2ServiceItem_list_request
    $serviceDefinition2Crane_list_request: serviceDefinition2Crane_list_request
  ) {
    service_definition(service_definition_list_request: $service_definition_list_request) {
      edges {
        id
        name
        status
        sequence
        due_at
      }
      totalCount
    }
    serviceDefinition2ServiceItem(serviceDefinition2ServiceItem_list_request: $serviceDefinition2ServiceItem_list_request) {
      edges {
        id
        service_item_id
      }
      totalCount
    }
    serviceDefinition2Crane(serviceDefinition2Crane_list_request: $serviceDefinition2Crane_list_request) {
      edges {
        id
        crane_id
        service_definition_id
        crane {
          id
          name
          serial_no
          longitude
          latitude
        }
      }
      totalCount
    }
  }
`;

const allServiceItemsQuery = gql`
  query allServiceItems($service_item_list_request: service_item_list_request) {
    service_item(service_item_list_request: $service_item_list_request) {
      edges {
        id
        name
        sequence
        service_section {
          id
          name
        }
        service_group {
          id
          name
        }
      }
      totalCount
    }
  }
`;

const allCranesQuery = gql`
  query allCranes($crane_list_request: crane_list_request) {
    crane(crane_list_request: $crane_list_request) {
      edges {
        id
        name
        serial_no
        longitude
        latitude
      }
      totalCount
    }
  }
`;

const entityMutation = gql`
  mutation service_definitionMutate($service_definition_mutation_request: MutationInput) {
    service_definition(service_definition_mutation_request: $service_definition_mutation_request) {
      id
      isNew
    }
  }
`;

const serviceDefinition2ServiceItemMutation = gql`
  mutation serviceDefinition2ServiceItemMutate($serviceDefinition2ServiceItem_mutation_request: MutationInput) {
    serviceDefinition2ServiceItem(serviceDefinition2ServiceItem_mutation_request: $serviceDefinition2ServiceItem_mutation_request) {
      id
      isNew
    }
  }
`;

const serviceDefinition2CraneMutation = gql`
  mutation serviceDefinition2CraneMutate($serviceDefinition2Crane_mutation_request: MutationInput) {
    serviceDefinition2Crane(serviceDefinition2Crane_mutation_request: $serviceDefinition2Crane_mutation_request) {
      id
      isNew
    }
  }
`;

function formatCraneLocation(cr) {
  if (!cr) return "—";
  const lat = cr.latitude;
  const lon = cr.longitude;
  if (lat == null || lon == null || lat === "" || lon === "") return "—";
  const nlat = Number(lat);
  const nlon = Number(lon);
  if (Number.isNaN(nlat) || Number.isNaN(nlon)) return "—";
  return `${nlat.toFixed(4)}, ${nlon.toFixed(4)}`;
}

export default function ServiceDefinitionEdit({ id: entityId, action, navigate }) {
  const client = useApolloClient();
  const isEdit = action === "edit";
  const { addNotification } = useContext(NotificationContext);

  const [name, setName] = useState('');
  const [sequence, setSequence] = useState();
  const [status, setStatus] = useState(isEdit ? 0 : 1);
  const [dueAt, setDueAt] = useState(null);
  const [invalidReasons, setInvalidReasons] = useState([]);
  const createKeys = useCreateIdempotencyKeys();
  const definitionProperties = [
    { key: "name", string: name },
    { key: "sequence", string: String(sequence) },
    { key: "status", number: status ? 1 : 0 },
    ...(dueAt === undefined || dueAt === null || dueAt === ""
      ? []
      : [{ key: "due_at", string: String(dueAt) }]),
  ];

  // Service items state
  const [originalLinks, setOriginalLinks] = useState([]);
  const [originalCraneLinks, setOriginalCraneLinks] = useState([]);
  const [selectedCraneIds, setSelectedCraneIds] = useState(new Set());
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [allServiceItems, setAllServiceItems] = useState([]);
  const [allCranes, setAllCranes] = useState([]);
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [craneSearchTerm, setCraneSearchTerm] = useState('');

  const variables = {
    service_definition_list_request: {
      pagination: { limit: 1, offset: 0 },
      filters: [
        {
          field: "id",
          operator: "=",
          value: String(entityId ?? 0),
        },
      ],
    },
    serviceDefinition2ServiceItem_list_request: {
      pagination: { limit: 10000, offset: 0 },
      filters: [{
        field: "service_definition_id",
        operator: "=",
        value: String(entityId ?? 0),
      }]
    },
    serviceDefinition2Crane_list_request: {
      pagination: { limit: 10000, offset: 0 },
      filters: [{
        field: "service_definition_id",
        operator: "=",
        value: String(entityId ?? 0),
      }]
    },
  };

  const { loading: loadingEntity, refetch: refetchEntity } = useQuery(entityQuery, {
    variables,
    skip: !isEdit && !entityId,
    fetchPolicy: "network-only",
    onCompleted: (localData) => {
      const listResponse = localData?.service_definition;
      const serviceItems = localData?.serviceDefinition2ServiceItem?.edges ?? [];
      const list = listResponse?.edges ?? [];

      if (list.length > 0) {
        const service_definition = list[0];
        setName(service_definition.name);
        setSequence(service_definition.sequence);
        setStatus(service_definition.status);
        setDueAt(service_definition.due_at);
      }

      // Set original links and selected item IDs from existing links for multi-select
      setOriginalLinks(serviceItems.map(si => ({
        id: si.id,
        service_item_id: Number(si.service_item_id)
      })));

      const existingItemIds = new Set(serviceItems.map(si => Number(si.service_item_id)).filter(id => id && !isNaN(id)));
      setSelectedItemIds(existingItemIds);

      // Set original crane links and selected crane IDs from the mapping table
      const craneEdges = localData?.serviceDefinition2Crane?.edges ?? [];
      setOriginalCraneLinks(
        craneEdges.map(edge => ({
          id: edge.id,
          crane_id: Number(edge.crane_id),
        }))
      );
      const craneIdSet = new Set(
        craneEdges.map(edge => Number(edge.crane_id)).filter(id => id && !isNaN(id))
      );
      setSelectedCraneIds(craneIdSet);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  // Query all service items for multi-select
  const { data: allItemsData, loading: loadingAllItems } = useQuery(allServiceItemsQuery, {
    variables: {
      service_item_list_request: {
        pagination: { limit: 10000 },
        filters: [],
        sorting: [{ field: "name", direction: "ASC" }]
      }
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const items = data?.service_item?.edges ?? [];
      setAllServiceItems(items);
    },
    onError: (error) => {
      console.error("Error fetching all service items:", error);
    },
  });

  const { loading: loadingAllCranes } = useQuery(allCranesQuery, {
    variables: {
      crane_list_request: {
        pagination: { limit: 10000 },
        filters: [],
        sorting: [{ field: "id", direction: "ASC" }]
      }
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const edges = data?.crane?.edges ?? [];
      setAllCranes(edges);
    },
    onError: (error) => {
      console.error("Error fetching all cranes:", error);
    },
  });

  const toggleCraneSelection = (craneId) => {
    const normalizedId = Number(craneId);
    if (!normalizedId || Number.isNaN(normalizedId)) return;
    setSelectedCraneIds((prev) => {
      const next = new Set(prev);
      if (next.has(normalizedId)) next.delete(normalizedId);
      else next.add(normalizedId);
      return next;
    });
  };

  const filteredCranes = allCranes.filter((cr) => {
    if (!craneSearchTerm) return true;
    const t = craneSearchTerm.toLowerCase();
    const loc = formatCraneLocation(cr);
    return (
      (cr.name || "").toLowerCase().includes(t) ||
      String(cr.serial_no || "").toLowerCase().includes(t) ||
      loc.toLowerCase().includes(t)
    );
  });

  const toggleItemSelection = (itemId) => {
    const newSelected = new Set(selectedItemIds);
    const normalizedId = Number(itemId);
    if (newSelected.has(normalizedId)) {
      newSelected.delete(normalizedId);
    } else {
      newSelected.add(normalizedId);
    }
    setSelectedItemIds(newSelected);
  };

  const removeSelectedItem = (itemId) => {
    const newSelected = new Set(selectedItemIds);
    const normalizedId = Number(itemId);
    newSelected.delete(normalizedId);
    setSelectedItemIds(newSelected);
  };

  const filteredItems = allServiceItems.filter(item => {
    if (!itemSearchTerm) return true;
    const searchLower = itemSearchTerm.toLowerCase();
    return (
      item.name?.toLowerCase().includes(searchLower) ||
      item.service_section?.name?.toLowerCase().includes(searchLower) ||
      item.service_group?.name?.toLowerCase().includes(searchLower)
    );
  });

  const selectedItems = allServiceItems.filter(item => selectedItemIds.has(Number(item.id)));

  const validateForm = () => {
    const reasons = [];

    if (!name || name.trim() === "") reasons.push("Name is required.");
    if (sequence === undefined || sequence === null || sequence === "") reasons.push("Order (Sequence) is required.");
    if (isNaN(Number(sequence))) reasons.push("Order (Sequence) must be a valid number.");
    // Due every (hrs) is optional; only validate when provided.
    if (dueAt !== undefined && dueAt !== null && dueAt !== "") {
      if (isNaN(Number(dueAt))) reasons.push("Due every (hrs) must be a valid number.");
    }

    setInvalidReasons(reasons);
    return reasons.length === 0;
  };


  const saveAll = async (service_definition_id) => {
    try {
      // Create links for selected items that don't already have links
      const existingItemIds = new Set(originalLinks.map(link => Number(link.service_item_id)));
      const newItemIds = Array.from(selectedItemIds).filter(id => !existingItemIds.has(Number(id)));

      const createPromises = newItemIds.map(itemId => {
        return client.mutate({
          mutation: serviceDefinition2ServiceItemMutation,
          variables: {
            serviceDefinition2ServiceItem_mutation_request: mutationInput({
              properties: [
                { key: "service_definition_id", number: Number(service_definition_id) },
                { key: "service_item_id", number: Number(itemId) },
              ],
              idempotencyKey: createKeys.keyFor(
                `item:${itemId}`,
                `${service_definition_id}:${itemId}`
              ),
            }),
          },
        });
      });

      await Promise.all(createPromises);

      // Delete links for items that were deselected
      const deletePromises = originalLinks
        .filter(link => !selectedItemIds.has(Number(link.service_item_id)))
        .map(link =>
          client.mutate({
            mutation: serviceDefinition2ServiceItemMutation,
            variables: {
              serviceDefinition2ServiceItem_mutation_request: {
                id: link.id,
                properties: [
                  { key: "service_definition_id", number: Number(service_definition_id) },
                  { key: "service_item_id", number: Number(link.service_item_id) },
                  { key: "delete", number: 1 },
                ],
              },
            },
          })
        );

      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Error saving service items:", error);
      addNotification("bad", "Error saving service items");
    }
  };

  const saveCraneLinks = async (service_definition_id) => {
    try {
      const selectedIds = Array.from(selectedCraneIds).map((id) => Number(id)).filter((id) => id && !isNaN(id));
      const selectedSet = new Set(selectedIds);

      const existingCraneIdsSet = new Set(
        originalCraneLinks.map((link) => Number(link.crane_id)).filter((id) => id && !isNaN(id))
      );

      // Create missing links
      const newCraneIds = selectedIds.filter((craneId) => !existingCraneIdsSet.has(craneId));
      const createPromises = newCraneIds.map((craneId) => {
        return client.mutate({
          mutation: serviceDefinition2CraneMutation,
          variables: {
            serviceDefinition2Crane_mutation_request: mutationInput({
              properties: [
                { key: "service_definition_id", number: Number(service_definition_id) },
                { key: "crane_id", number: Number(craneId) },
              ],
              idempotencyKey: createKeys.keyFor(
                `crane:${craneId}`,
                `${service_definition_id}:${craneId}`
              ),
            }),
          },
        });
      });
      await Promise.all(createPromises);

      // Delete links for deselected cranes
      const deletePromises = originalCraneLinks
        .filter((link) => !selectedSet.has(Number(link.crane_id)))
        .map((link) =>
          client.mutate({
            mutation: serviceDefinition2CraneMutation,
            variables: {
              serviceDefinition2Crane_mutation_request: {
                id: link.id,
                properties: [
                  { key: "service_definition_id", number: Number(service_definition_id) },
                  { key: "crane_id", number: Number(link.crane_id) },
                  { key: "delete", number: 1 },
                ],
              },
            },
          })
        );
      await Promise.all(deletePromises);
    } catch (error) {
      console.error("Error saving serviceDefinition2Crane links:", error);
      addNotification("bad", "Error saving Assigned Cranes");
    }
  };

  const [mutateFunction, mutateInfo] = useMutation(entityMutation, {
    variables: {
      service_definition_mutation_request: mutationInput({
        id: isEdit ? entityId : undefined,
        properties: definitionProperties,
        idempotencyKey: isEdit
          ? undefined
          : createKeys.keyFor("definition", fingerprintProperties(definitionProperties)),
      }),
    },
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
    onCompleted: async (response) => {
      const id = response?.service_definition?.id;
      await saveAll(id);
      if (isEdit) {
        await saveCraneLinks(id);
      }
      addNotification("good", "Service Definition saved successfully");
      navigate({
        action: "edit",
        id,
        push: false,
      });
      refetchEntity();
    },
  });

  useEffect(() => {
    if (isEdit && entityId) {
      refetchEntity();
    }
  }, [entityId, isEdit]);

  let loadingMutation = mutateInfo.loading;

  return loadingEntity ? (
    <LoadingView />
  ) : (
    <>
      <div className="page-header">
        <span className="page-header-title">
          { isEdit ? "Edit" : "Add" } Service Definition Detail
        </span>
      </div>

      <div className="add-edit">
        <div className="edit-section">
          <div className="edit-property">
            <label>Status:</label>
            <ToggleSwitch
              checked={ status }
              onChange={ () => setStatus(!status) }
              size={ {
                width: 60,
                height: 32,
                knob: 24,
                padding: 4
              } }
            />
          </div>
          <div className="edit-property">
            <label>
              Name *:
            </label>
            <input value={ name } onChange={ (e) => setName(e.target.value) } />
          </div>
          <div className="edit-property">
            <label>
              Order *:
            </label>
            <input value={ sequence } onChange={ (e) => setSequence(e.target.value) } />
          </div>
          <div className="edit-property">
            <label>Due every (hrs):</label>
            <input
              type="number"
              value={ dueAt ?? "" }
              onChange={ (e) => setDueAt(e.target.value) }
            />
          </div>
        </div>

        { isEdit && (
          <div className="edit-section" style={ { marginTop: '20px', padding: '15px', background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px' } }>
            <label>Assigned Cranes</label>
            <div style={ { marginTop: '12px' } }>
              <div style={ { display: "flex", gap: "10px", alignItems: "center" } }>
                <input
                  type="text"
                  value={ craneSearchTerm }
                  onChange={ (e) => setCraneSearchTerm(e.target.value) }
                  placeholder="Search cranes by name, serial"
                  style={ { flex: 1, padding: "8px 12px", border: "1px solid #ced4da", borderRadius: "4px" } }
                />
              </div>

              { loadingAllCranes ? (
                <div style={ { padding: "20px", textAlign: "center", color: "#6c757d" } }>
                  Loading cranes...
                </div>
              ) : (
                <>
                  <div style={ { marginTop: 12, maxHeight: 220, overflowY: "auto", border: "1px solid #dee2e6", borderRadius: 4, padding: 8, background: "#f8f9fa" } }>
                    { filteredCranes.length === 0 ? (
                      <div style={ { padding: "10px", textAlign: "center", color: "#6c757d" } }>
                        { craneSearchTerm ? "No cranes match your search." : "No cranes available." }
                      </div>
                    ) : (
                      filteredCranes.map((cr) => (
                        <ItemCheckbox key={ cr.id }>
                          <input
                            type="checkbox"
                            checked={ selectedCraneIds.has(Number(cr.id)) }
                            onChange={ () => toggleCraneSelection(cr.id) }
                          />
                          <div className="item-info">
                            <div className="item-name">{ cr.name || `Crane ${cr.id}` }</div>
                            <div className="item-meta">
                              <span>Serial: { cr.serial_no != null && String(cr.serial_no).trim() !== "" ? cr.serial_no : "—" }</span>
                              <span>Location: { formatCraneLocation(cr) }</span>
                            </div>
                          </div>
                        </ItemCheckbox>
                      ))
                    ) }
                  </div>
                </>
              ) }

              <div style={ { marginTop: 14 } }>
                { selectedCraneIds.size > 0 ? (
                  <SelectedItems>
                    <h4>Selected Cranes ({ selectedCraneIds.size })</h4>
                    <div style={ { display: "flex", flexWrap: "wrap", gap: "4px" } }>
                      { Array.from(selectedCraneIds)
                        .map((id) => allCranes.find((c) => Number(c.id) === Number(id)))
                        .filter(Boolean)
                        .map((cr) => (
                          <SelectedItemTag key={ cr.id }>
                            { cr.name || `Crane ${cr.id}` }
                            <button
                              type="button"
                              onClick={ () => toggleCraneSelection(cr.id) }
                              title="Remove"
                            >
                              <FaTimes />
                            </button>
                          </SelectedItemTag>
                        )) }
                    </div>
                  </SelectedItems>
                ) : (
                  <p style={ { marginTop: "8px", color: "#6c757d", fontSize: "14px" } }>
                    No cranes assigned to this service definition.
                  </p>
                ) }
              </div>
            </div>
          </div>
        ) }

        <MultiSelectContainer>
          <MultiSelectHeader>
            <h3>Assign Service Items</h3>
          </MultiSelectHeader>

          <SearchInput
            type="text"
            placeholder="Search items by name, section, or group..."
            value={ itemSearchTerm }
            onChange={ (e) => setItemSearchTerm(e.target.value) }
          />

          { loadingAllItems ? (
            <div style={ { padding: '20px', textAlign: 'center', color: '#6c757d' } }>Loading items...</div>
          ) : (
            <>
              <ItemsList>
                { filteredItems.length === 0 ? (
                  <div style={ { padding: '20px', textAlign: 'center', color: '#6c757d' } }>
                    { itemSearchTerm ? 'No items match your search.' : 'No service items available.' }
                  </div>
                ) : (
                  filteredItems.map(item => (
                    <ItemCheckbox key={ item.id }>
                      <input
                        type="checkbox"
                        checked={ selectedItemIds.has(Number(item.id)) }
                        onChange={ () => toggleItemSelection(item.id) }
                      />
                      <div className="item-info">
                        <div className="item-name">{ item.name }</div>
                        <div className="item-meta">
                          { item.service_section?.name && (
                            <span>Section: { item.service_section.name }</span>
                          ) }
                          { item.service_group?.name && (
                            <span>Group: { item.service_group.name }</span>
                          ) }
                        </div>
                      </div>
                    </ItemCheckbox>
                  ))
                ) }
              </ItemsList>

              { selectedItems.length > 0 && (
                <SelectedItems>
                  <h4>Selected Items ({ selectedItems.length })</h4>
                  <div style={ { display: 'flex', flexWrap: 'wrap', gap: '4px' } }>
                    { selectedItems.map(item => (
                      <SelectedItemTag key={ item.id }>
                        { item.name }
                        <button
                          type="button"
                          onClick={ () => removeSelectedItem(item.id) }
                          title="Remove"
                        >
                          <FaTimes />
                        </button>
                      </SelectedItemTag>
                    )) }
                  </div>
                </SelectedItems>
              ) }
            </>
          ) }
        </MultiSelectContainer>

        { invalidReasons.length > 0 && (
          <InvalidReasons reasons={ invalidReasons } />
        ) }
        <div className="edit-section">
          <SaveButton
            saving={ !isEdit && createKeys.isSaving }
            disabled={ isEdit && loadingMutation }
            onClick={ () => {
              if (!validateForm()) return;
              if (isEdit) mutateFunction();
              else createKeys.trackSave(() => mutateFunction());
            } }
          />
        </div>
      </div>
    </>
  );
}
